import type { BodyScrollEvent, TooltipShowEvent } from 'ag-grid-community'

import type { HonestGridApi } from '../types'
import { ROW_HEIGHT } from '../ui'

/**
 * Manifest scroll depth at the current scroll position, shared by the inline
 * (useManifestTracking) and fullscreen (useManifestFullscreenTracking)
 * trackers. Both axes count what has scrolled *into* view since rest, with a
 * half-visible threshold: the trailing edge (right for columns, bottom for
 * rows) starts at the viewport size, so rows/columns visible without scrolling
 * don't count, and a row/column is counted once that edge crosses its midpoint.
 * - columns: how many columns have entered past the right edge (0 unscrolled)
 * - rows: how many rows have entered past the bottom edge (0 unscrolled)
 *
 * An axis whose pixel range isn't available yet (before layout) reports 0. The
 * inline tracker reads only `columns`; computing `rows` for it is harmless.
 */
export const computeScrollDepth = (
  e: BodyScrollEvent
): { columns: number; rows: number } => {
  const api: HonestGridApi = e.api

  const scrollableColumns = api
    .getAllDisplayedColumns()
    .filter((c) => !c.getPinned())
  const hRange = api.getHorizontalPixelRange()
  let columns = 0
  if (hRange) {
    // Right edge at rest = viewport width; columns left of it were already in
    // view. Count those the right edge has since crossed the midpoint of.
    const restRight = hRange.right - hRange.left
    let start = 0
    for (const col of scrollableColumns) {
      const width = col.getActualWidth()
      const midpoint = start + width / 2
      if (midpoint > hRange.right) break
      if (midpoint > restRight) columns++
      start += width
    }
  }

  // Same logic for rows, closed-form since every row is ROW_HEIGHT tall:
  // round(edge / ROW_HEIGHT) is the count of row midpoints above `edge`, so the
  // difference is how many the bottom edge has crossed past its at-rest spot.
  const vRange = api.getVerticalPixelRange()
  let rows = 0
  if (vRange && vRange.bottom > vRange.top) {
    const restBottom = vRange.bottom - vRange.top
    rows =
      Math.round(vRange.bottom / ROW_HEIGHT) -
      Math.round(restBottom / ROW_HEIGHT)
  }

  return { columns, rows }
}

/** Column id and row index behind a manifest grid tooltip, from its DOM. */
export const getTooltipTarget = (
  e?: TooltipShowEvent
): { column: string | null | undefined; rowIndex: number | undefined } => {
  const column = e?.parentGui.getAttribute('col-id')
  const rowIndexAttr = e?.parentGui
    .closest('[row-index]')
    ?.getAttribute('row-index')
  const rowIndex =
    typeof rowIndexAttr === 'string' ? Number(rowIndexAttr) : undefined
  return { column, rowIndex }
}

/**
 * Stable key for a tooltip target, so repeated tooltipShow events for the
 * same cell (ag-Grid can re-fire tooltipShow for a cell that's already
 * showing its tooltip, e.g. on a grid re-render or a small pointer move
 * within the same cell) can be deduped against the last-shown target.
 */
export const getTooltipTargetKey = (
  column: string | null | undefined,
  rowIndex: number | undefined
): string => `${column ?? ''}::${rowIndex ?? ''}`
